import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import moment, { Moment } from 'moment';

import { enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';
import { updateNfcCardExpiration } from 'lib/models/nfcCards';

import useNfcCardLabels from 'hooks/useNfcCardLabels';

type Props = {
	card: NfcCard;
	/** Called when the dialog closes, telling whether the expiration changed, so the listing is read again. */
	onClose: (changed: boolean) => void;
};

/**
 * Changes the expiration of a card, or takes it away, which is how an expired card is used
 * again (MUTE-622). As in a new batch, the card is usable through the whole day picked. An
 * expiration that already went by starts empty, since the API only takes a date ahead.
 */
export default function NfcCardExpirationDialog({ card, onClose }: Props) {
	const { t } = useTranslation();
	const { cardName } = useNfcCardLabels();

	const [expiresAt, setExpiresAt] = useState<Moment | null>(() => {
		const current = card.expiresAt ? moment(card.expiresAt) : null;
		return current && current.isAfter(moment()) ? current : null;
	});
	const [saving, setSaving] = useState(false);

	// A card is usable through the whole day it is set to expire on, so today is still fine
	const brokenDate = !!expiresAt && (!expiresAt.isValid() || expiresAt.isBefore(moment().startOf('day')));

	const onSave = async () => {
		setSaving(true);
		try {
			await updateNfcCardExpiration(card._id, expiresAt ? expiresAt.clone().endOf('day').toISOString() : null);
			enqueueSnackbarSuccess(t('nfc:expirationSaved'));
			onClose(true);
		} catch (error) {
			enqueueSnackbarError(error);
			setSaving(false);
		}
	};

	return (
		<Dialog open onClose={() => !saving && onClose(false)} fullWidth maxWidth="xs">
			{saving && <LinearProgress />}
			<DialogTitle>{t('nfc:expirationTitle', { card: cardName(card.label) })}</DialogTitle>
			<DialogContent>
				<DialogContentText sx={{ mb: 3 }}>{t('nfc:expirationDescription')}</DialogContentText>
				<DatePicker
					sx={{ width: '100%' }}
					value={expiresAt}
					label={t('nfc:expiresAt')}
					format="DD/MM/YYYY"
					disablePast
					onChange={(value) => setExpiresAt(value)}
					disabled={saving}
					slotProps={{
						field: { clearable: true },
						textField: { error: brokenDate, helperText: brokenDate ? t('nfc:batchDateInvalid') : undefined },
					}}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => onClose(false)} color="secondary" disabled={saving}>
					{t('common:cancel')}
				</Button>
				<Button onClick={onSave} color="primary" disabled={saving || brokenDate}>
					{t('nfc:expirationSave')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
